import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Gem,
  Loader2,
  Minus,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Swords,
  X,
} from "lucide-react";

import { SkillUsageTags } from "@/components/story/SkillUsageTags.tsx";
import { useStoryPlaygroundContext } from "@/components/story/StoryPlaygroundContext.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Card } from "@/components/ui/card.tsx";
import { Input } from "@/components/ui/input.tsx";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";

type EditTarget =
  | { kind: "storyName" }
  | { kind: "storyIntro" }
  | { kind: "mainObjective" }
  | { kind: "twist" }
  | { kind: "sideQuest"; index: number }
  | { kind: "encounter"; index: number }
  | { kind: "character"; index: number }
  | { kind: "location"; index: number };

type PrefillField = "sideQuests" | "encounters" | "characters" | "locations";

type EncounterTypeMeta = {
  label: string;
  Icon: typeof Sparkles;
};

const renderEmpty = (text: string) => <span className="text-sm text-muted-foreground">{text}</span>;

const renderSkeletonCards = (count: number) =>
  Array.from(
    { length: count },
    (_, index) => (
      <Card key={`skeleton-${index}`} className="p-4 space-y-2">
        <div className="h-3 w-24 animate-pulse rounded bg-muted" />
        <div className="h-4 w-full animate-pulse rounded bg-muted" />
        <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
      </Card>
    ),
  );

export function StoryPlayground() {
  const { t } = useTranslation();
  const {
    settings,
    storySeed,
    drafts,
    generatingFields,
    onGenerateTwist,
    onGenerateIntro,
    onGenerateSideQuest,
    onGenerateCharacter,
    onGenerateLocation,
    onGenerateEncounter,
    onGenerateStoryNames,
    onGenerateMainObjective,
    onGenerateStep4Bulk,
    onSelectSuggestion,
    onToggleLock,
    onUpdateSettings,
    onUpdateStoryName,
    onUpdateStoryIntro,
    onUpdateSideQuest,
    onUpdateCharacter,
    onUpdateLocation,
    onUpdateEncounter,
    onUpdateSlotCount,
    onUpdateMainObjective,
    onUpdateStoryTwist,
    onGenerateSkillUses,
    onBack,
  } = useStoryPlaygroundContext();

  const [activeSkillUseLoaders, setActiveSkillUseLoaders] = useState<Record<string, boolean>>({});
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [pendingPrefill, setPendingPrefill] = useState<PrefillField | null>(null);
  const hasAutoPopulatedRef = useRef(false);
  const autoPopulatedFieldsRef = useRef({
    storyName: false,
    storyIntro: false,
    storyTwist: false,
    sideQuests: false,
    characters: false,
    locations: false,
    encounters: false,
  });
  const requestedSkillUseKeysRef = useRef(new Set<string>());

  const sideQuestCount = storySeed.sideQuests.filter((q) => q.description?.trim()).length;
  const encounterCount = storySeed.encounters.filter((e) => e.description?.trim()).length;
  const characterCount = storySeed.characters.filter((c) => c.name?.trim()).length;
  const locationCount = storySeed.locations.filter((l) => l.name?.trim()).length;
  const canAddSkillUses = settings.lockedSkills.length > 0;
  const skillsTitle = settings.mainCharacterName
    ? `${settings.mainCharacterName}'s Skills`
    : t("playground.characterSkills", { defaultValue: "Character Skills" });

  const visibleSideQuests = storySeed.sideQuests
    .map((quest, index) => ({ quest, index }))
    .filter(({ quest }) => quest.description?.trim());

  const isInitialBulkLoading = Boolean(generatingFields.content);

  const shouldAutoPopulate = useMemo(() => {
    const hasObjective = (storySeed.mainObjective.description ?? "").trim().length > 0;
    const hasGeneratedCore = Boolean(
      storySeed.storyName.trim() ||
        storySeed.storyIntro.trim() ||
        storySeed.storyTwist.trim() ||
        storySeed.sideQuests.some((quest) => quest.description?.trim()) ||
        storySeed.characters.some((character) => character.name?.trim()) ||
        storySeed.locations.some((location) => location.name?.trim()) ||
        storySeed.encounters.some((encounter) => encounter.description?.trim()),
    );
    return hasObjective && !hasGeneratedCore;
  }, [storySeed]);

  const withSkillUsageLoader = async (key: string, action: () => Promise<void>) => {
    setActiveSkillUseLoaders((prev) => ({ ...prev, [key]: true }));
    try {
      await action();
    } finally {
      setActiveSkillUseLoaders((prev) => ({ ...prev, [key]: false }));
    }
  };

  const appendSkillUses = (existing: string[], next: string[]) => {
    const seen = new Set(existing.map((value) => value.trim().toLowerCase()));
    const merged = [...existing];
    for (const value of next) {
      const trimmed = value.trim();
      const key = trimmed.toLowerCase();
      if (!trimmed || seen.has(key)) continue;
      seen.add(key);
      merged.push(trimmed);
    }
    return merged;
  };

  const removeSkillUse = (items: string[], value: string) => {
    const index = items.indexOf(value);
    if (index < 0) return items;
    return [...items.slice(0, index), ...items.slice(index + 1)];
  };

  const isCandidateUsed = (
    field: PrefillField,
    item: { description?: string; name?: string; role?: string },
  ) => {
    if (field === "sideQuests") {
      const key = item.description?.trim().toLowerCase();
      if (!key) return true;
      return storySeed.sideQuests.some((quest) => quest.description?.trim().toLowerCase() === key);
    }

    if (field === "encounters") {
      const key = item.description?.trim().toLowerCase();
      if (!key) return true;
      return storySeed.encounters.some((encounter) =>
        encounter.description?.trim().toLowerCase() === key
      );
    }

    if (field === "characters") {
      const nameKey = item.name?.trim().toLowerCase() ?? "";
      const roleKey = item.role?.trim().toLowerCase() ?? "";
      if (!nameKey) return true;
      return storySeed.characters.some((character) =>
        (character.name?.trim().toLowerCase() ?? "") === nameKey &&
        (character.role?.trim().toLowerCase() ?? "") === roleKey
      );
    }

    const key = item.name?.trim().toLowerCase();
    if (!key) return true;
    return storySeed.locations.some((location) => location.name?.trim().toLowerCase() === key);
  };

  const findUnusedPrefillSuggestion = useCallback((field: PrefillField) => {
    if (field === "sideQuests") {
      return drafts.sideQuests.options.find((item) => !isCandidateUsed(field, item)) ?? null;
    }
    if (field === "encounters") {
      return drafts.encounters.options.find((item) => !isCandidateUsed(field, item)) ?? null;
    }
    if (field === "characters") {
      return drafts.characters.options.find((item) => !isCandidateUsed(field, item)) ?? null;
    }
    return drafts.locations.options.find((item) => !isCandidateUsed(field, item)) ?? null;
  }, [
    drafts.characters.options,
    drafts.encounters.options,
    drafts.locations.options,
    drafts.sideQuests.options,
    storySeed.characters,
    storySeed.encounters,
    storySeed.locations,
    storySeed.sideQuests,
  ]);

  const getEncounterTypeMeta = (type?: string): EncounterTypeMeta => {
    const normalized = (type ?? "").trim().toLowerCase();
    if (!normalized) {
      return {
        label: t("playground.encounterTypeDefault", { defaultValue: "Encounter" }),
        Icon: Sparkles,
      };
    }

    if (
      normalized.includes("monster") || normalized.includes("creature") ||
      normalized.includes("enemy") || normalized.includes("combat") || normalized.includes("battle")
    ) {
      return {
        label: t("playground.encounterTypeMonster", { defaultValue: "Monster" }),
        Icon: Swords,
      };
    }

    if (
      normalized.includes("treasure") || normalized.includes("loot") ||
      normalized.includes("artifact") || normalized.includes("reward") ||
      normalized.includes("item")
    ) {
      return {
        label: t("playground.encounterTypeTreasure", { defaultValue: "Treasure" }),
        Icon: Gem,
      };
    }

    if (
      normalized.includes("puzzle") || normalized.includes("riddle") ||
      normalized.includes("mystery")
    ) {
      return {
        label: t("playground.encounterTypePuzzle", { defaultValue: "Puzzle" }),
        Icon: Search,
      };
    }

    return {
      label: t("playground.encounterTypeEvent", { defaultValue: "Event" }),
      Icon: AlertTriangle,
    };
  };

  useEffect(() => {
    if (!pendingPrefill) return;

    if (pendingPrefill === "sideQuests") {
      const hasEmpty = storySeed.sideQuests.some((quest) => !quest.description?.trim());
      const first = findUnusedPrefillSuggestion("sideQuests");
      if (!hasEmpty || !first) return;
      onSelectSuggestion("sideQuests", first);
      setPendingPrefill(null);
      return;
    }

    if (pendingPrefill === "encounters") {
      const hasEmpty = storySeed.encounters.some((encounter) => !encounter.description?.trim());
      const first = findUnusedPrefillSuggestion("encounters");
      if (!hasEmpty || !first) return;
      onSelectSuggestion("encounters", first);
      setPendingPrefill(null);
      return;
    }

    if (pendingPrefill === "characters") {
      const hasEmpty = storySeed.characters.some((character) => !character.name?.trim());
      const first = findUnusedPrefillSuggestion("characters");
      if (!hasEmpty || !first) return;
      onSelectSuggestion("characters", first);
      setPendingPrefill(null);
      return;
    }

    if (pendingPrefill === "locations") {
      const hasEmpty = storySeed.locations.some((location) => !location.name?.trim());
      const first = findUnusedPrefillSuggestion("locations");
      if (!hasEmpty || !first) return;
      onSelectSuggestion("locations", first);
      setPendingPrefill(null);
    }
  }, [
    drafts.characters.options,
    drafts.encounters.options,
    drafts.locations.options,
    drafts.sideQuests.options,
    findUnusedPrefillSuggestion,
    onSelectSuggestion,
    pendingPrefill,
    storySeed.characters,
    storySeed.encounters,
    storySeed.locations,
    storySeed.sideQuests,
  ]);

  const handleAddPrefilledSlot = async (field: PrefillField) => {
    if (field === "sideQuests") {
      const hasEmpty = storySeed.sideQuests.some((quest) => !quest.description?.trim());
      if (!hasEmpty) onUpdateSlotCount("sideQuests", 1);
      setPendingPrefill(field);
      if (!findUnusedPrefillSuggestion(field)) await onGenerateSideQuest("append", false);
      return;
    }

    if (field === "encounters") {
      const hasEmpty = storySeed.encounters.some((encounter) => !encounter.description?.trim());
      if (!hasEmpty) onUpdateSlotCount("encounters", 1);
      setPendingPrefill(field);
      if (!findUnusedPrefillSuggestion(field)) await onGenerateEncounter("append", false);
      return;
    }

    if (field === "characters") {
      const hasEmpty = storySeed.characters.some((character) => !character.name?.trim());
      if (!hasEmpty) onUpdateSlotCount("characters", 1);
      setPendingPrefill(field);
      if (!findUnusedPrefillSuggestion(field)) await onGenerateCharacter("append", false);
      return;
    }

    const hasEmpty = storySeed.locations.some((location) => !location.name?.trim());
    if (!hasEmpty) onUpdateSlotCount("locations", 1);
    setPendingPrefill(field);
    if (!findUnusedPrefillSuggestion(field)) await onGenerateLocation("append", false);
  };

  useEffect(() => {
    if (!shouldAutoPopulate || hasAutoPopulatedRef.current) return;

    hasAutoPopulatedRef.current = true;
    if (settings.slotCounts.sideQuests < 2) {
      onUpdateSlotCount("sideQuests", 2 - settings.slotCounts.sideQuests);
    }
    if (settings.slotCounts.characters < 3) {
      onUpdateSlotCount("characters", 3 - settings.slotCounts.characters);
    }
    void onGenerateStep4Bulk();
  }, [
    onGenerateStep4Bulk,
    settings.slotCounts.characters,
    settings.slotCounts.sideQuests,
    onUpdateSlotCount,
    shouldAutoPopulate,
  ]);

  useEffect(() => {
    if (!(storySeed.mainObjective.description ?? "").trim()) return;

    const firstName = drafts.storyName.options[0];
    if (!autoPopulatedFieldsRef.current.storyName) {
      if (storySeed.storyName.trim()) {
        autoPopulatedFieldsRef.current.storyName = true;
      } else if (firstName) {
        onUpdateStoryName(firstName);
        autoPopulatedFieldsRef.current.storyName = true;
      }
    }

    const firstIntro = drafts.storyIntro.options[0];
    if (!autoPopulatedFieldsRef.current.storyIntro) {
      if (storySeed.storyIntro.trim()) {
        autoPopulatedFieldsRef.current.storyIntro = true;
      } else if (firstIntro) {
        onUpdateStoryIntro(firstIntro);
        autoPopulatedFieldsRef.current.storyIntro = true;
      }
    }

    const firstTwist = drafts.storyTwist.options[0];
    if (!autoPopulatedFieldsRef.current.storyTwist) {
      if (storySeed.storyTwist.trim()) {
        autoPopulatedFieldsRef.current.storyTwist = true;
      } else if (firstTwist) {
        onUpdateStoryTwist(firstTwist);
        autoPopulatedFieldsRef.current.storyTwist = true;
      }
    }

    if (!autoPopulatedFieldsRef.current.sideQuests) {
      const emptySideQuestIndexes = storySeed.sideQuests
        .map((quest, index) => ({ quest, index }))
        .filter(({ quest }) => !quest.description?.trim())
        .map(({ index }) => index);

      if (emptySideQuestIndexes.length === 0) {
        autoPopulatedFieldsRef.current.sideQuests = true;
      } else if (drafts.sideQuests.options.length > 0) {
        drafts.sideQuests.options.slice(0, emptySideQuestIndexes.length).forEach(
          (quest, offset) => {
            onUpdateSideQuest(emptySideQuestIndexes[offset], {
              description: quest.description,
              skillUses: quest.skillUses ?? [],
            });
          },
        );
        autoPopulatedFieldsRef.current.sideQuests = true;
      }
    }

    if (!autoPopulatedFieldsRef.current.characters) {
      const emptyCharacterIndexes = storySeed.characters
        .map((character, index) => ({ character, index }))
        .filter(({ character }) => !character.name?.trim())
        .map(({ index }) => index);

      if (emptyCharacterIndexes.length === 0) {
        autoPopulatedFieldsRef.current.characters = true;
      } else if (drafts.characters.options.length > 0) {
        drafts.characters.options.slice(0, emptyCharacterIndexes.length).forEach(
          (character, offset) => {
            onUpdateCharacter(emptyCharacterIndexes[offset], {
              name: character.name,
              role: character.role,
              emoji: character.emoji,
              skillUses: character.skillUses ?? [],
            });
          },
        );
        autoPopulatedFieldsRef.current.characters = true;
      }
    }

    if (!autoPopulatedFieldsRef.current.locations) {
      const emptyLocationIndexes = storySeed.locations
        .map((location, index) => ({ location, index }))
        .filter(({ location }) => !location.name?.trim())
        .map(({ index }) => index);

      if (emptyLocationIndexes.length === 0) {
        autoPopulatedFieldsRef.current.locations = true;
      } else if (drafts.locations.options.length > 0) {
        drafts.locations.options.slice(0, emptyLocationIndexes.length).forEach(
          (location, offset) => {
            onUpdateLocation(emptyLocationIndexes[offset], {
              name: location.name,
              description: location.description,
              emoji: location.emoji,
              skillUses: location.skillUses ?? [],
            });
          },
        );
        autoPopulatedFieldsRef.current.locations = true;
      }
    }

    if (!autoPopulatedFieldsRef.current.encounters) {
      const emptyEncounterIndexes = storySeed.encounters
        .map((encounter, index) => ({ encounter, index }))
        .filter(({ encounter }) => !encounter.description?.trim())
        .map(({ index }) => index);

      if (emptyEncounterIndexes.length === 0) {
        autoPopulatedFieldsRef.current.encounters = true;
      } else if (drafts.encounters.options.length > 0) {
        drafts.encounters.options.slice(0, emptyEncounterIndexes.length).forEach(
          (encounter, offset) => {
            onUpdateEncounter(emptyEncounterIndexes[offset], {
              description: encounter.description,
              type: encounter.type,
              skillUses: encounter.skillUses ?? [],
            });
          },
        );
        autoPopulatedFieldsRef.current.encounters = true;
      }
    }
  }, [
    drafts.characters.options,
    drafts.encounters.options,
    drafts.locations.options,
    drafts.sideQuests.options,
    drafts.storyIntro.options,
    drafts.storyName.options,
    drafts.storyTwist.options,
    onUpdateCharacter,
    onUpdateEncounter,
    onUpdateLocation,
    onUpdateSideQuest,
    onUpdateStoryIntro,
    onUpdateStoryName,
    onUpdateStoryTwist,
    storySeed.characters,
    storySeed.encounters,
    storySeed.locations,
    storySeed.sideQuests,
    storySeed.mainObjective.description,
    storySeed.storyIntro,
    storySeed.storyName,
    storySeed.storyTwist,
  ]);

  useEffect(() => {
    if (!canAddSkillUses) return;

    const tasks: Promise<void>[] = [];

    const enqueue = (key: string, action: () => Promise<void>) => {
      if (requestedSkillUseKeysRef.current.has(key)) return;
      requestedSkillUseKeysRef.current.add(key);
      tasks.push(action());
    };

    if (
      (storySeed.mainObjective.description ?? "").trim() &&
      (storySeed.mainObjective.skillUses?.length ?? 0) === 0
    ) {
      enqueue("objective", async () => {
        const uses = await onGenerateSkillUses("objective", undefined, {
          forceFresh: false,
          targetDescription: storySeed.mainObjective.description,
        });
        if (uses.length > 0) {
          onUpdateMainObjective({ skillUses: uses });
        }
      });
    }

    storySeed.sideQuests.forEach((quest, index) => {
      if (!quest.description?.trim() || (quest.skillUses?.length ?? 0) > 0) return;
      enqueue(`sideQuest-${index}-${quest.description}`, async () => {
        const uses = await onGenerateSkillUses("sideQuest", index, {
          forceFresh: false,
          targetDescription: quest.description,
        });
        if (uses.length > 0) {
          onUpdateSideQuest(index, { skillUses: uses });
        }
      });
    });

    storySeed.characters.forEach((character, index) => {
      if (!character.name?.trim() || (character.skillUses?.length ?? 0) > 0) return;
      enqueue(`character-${index}-${character.name}-${character.role}`, async () => {
        const uses = await onGenerateSkillUses("character", index, {
          forceFresh: false,
          targetDescription: `${character.name} (${character.role})`,
        });
        if (uses.length > 0) {
          onUpdateCharacter(index, { skillUses: uses });
        }
      });
    });

    storySeed.locations.forEach((location, index) => {
      if (!location.name?.trim() || (location.skillUses?.length ?? 0) > 0) return;
      enqueue(`location-${index}-${location.name}-${location.description}`, async () => {
        const uses = await onGenerateSkillUses("location", index, {
          forceFresh: false,
          targetDescription: `${location.name} - ${location.description}`,
        });
        if (uses.length > 0) {
          onUpdateLocation(index, { skillUses: uses });
        }
      });
    });

    storySeed.encounters.forEach((encounter, index) => {
      if (!encounter.description?.trim() || (encounter.skillUses?.length ?? 0) > 0) return;
      enqueue(`encounter-${index}-${encounter.description}`, async () => {
        const uses = await onGenerateSkillUses("encounter", index, {
          forceFresh: false,
          targetDescription: encounter.description,
        });
        if (uses.length > 0) {
          onUpdateEncounter(index, { skillUses: uses });
        }
      });
    });

    if (tasks.length > 0) {
      void Promise.all(tasks);
    }
  }, [
    canAddSkillUses,
    onGenerateSkillUses,
    onUpdateCharacter,
    onUpdateEncounter,
    onUpdateLocation,
    onUpdateMainObjective,
    onUpdateSideQuest,
    storySeed.characters,
    storySeed.encounters,
    storySeed.locations,
    storySeed.mainObjective.description,
    storySeed.mainObjective.skillUses,
    storySeed.sideQuests,
  ]);

  const addMoreForTarget = async () => {
    if (!editTarget) return;
    if (editTarget.kind === "storyName") {
      await onGenerateStoryNames({ forceFresh: true });
      return;
    }
    if (editTarget.kind === "storyIntro") {
      await onGenerateIntro("append", true);
      return;
    }
    if (editTarget.kind === "mainObjective") {
      await onGenerateMainObjective({ forceFresh: true });
      return;
    }
    if (editTarget.kind === "twist") {
      await onGenerateTwist("append", true);
      return;
    }
    if (editTarget.kind === "sideQuest") {
      await onGenerateSideQuest("append", true);
      return;
    }
    if (editTarget.kind === "character") {
      await onGenerateCharacter("append", true);
      return;
    }
    if (editTarget.kind === "location") {
      await onGenerateLocation("append", true);
      return;
    }
    await onGenerateEncounter("append", true);
  };

  const renderSheetSuggestions = () => {
    if (!editTarget) return null;

    if (editTarget.kind === "storyName") {
      return drafts.storyName.options.map((item) => (
        <Button
          key={item}
          variant="outline"
          className="w-full justify-start text-left"
          onClick={() => onUpdateStoryName(item)}
        >
          {item}
        </Button>
      ));
    }

    if (editTarget.kind === "storyIntro") {
      return drafts.storyIntro.options.map((item) => (
        <Button
          key={item}
          variant="outline"
          className="h-auto w-full justify-start text-left whitespace-normal"
          onClick={() => onUpdateStoryIntro(item)}
        >
          {item}
        </Button>
      ));
    }

    if (editTarget.kind === "mainObjective") {
      return drafts.mainObjective.options.map((item) => (
        <Button
          key={item.description}
          variant="outline"
          className="h-auto w-full justify-start text-left whitespace-normal"
          onClick={() => onUpdateMainObjective({ description: item.description })}
        >
          {item.description}
        </Button>
      ));
    }

    if (editTarget.kind === "twist") {
      return drafts.storyTwist.options.map((item) => (
        <Button
          key={item}
          variant="outline"
          className="h-auto w-full justify-start text-left whitespace-normal"
          onClick={() => onUpdateStoryTwist(item)}
        >
          {item}
        </Button>
      ));
    }

    if (editTarget.kind === "sideQuest") {
      const index = editTarget.index;
      return drafts.sideQuests.options.map((item, suggestionIndex) => (
        <Button
          key={`${item.description}-${suggestionIndex}`}
          variant="outline"
          className="h-auto w-full justify-start text-left whitespace-normal"
          onClick={() => onUpdateSideQuest(index, { description: item.description })}
        >
          {item.description}
        </Button>
      ));
    }

    if (editTarget.kind === "character") {
      const index = editTarget.index;
      return drafts.characters.options.map((item, suggestionIndex) => (
        <Button
          key={`${item.name}-${suggestionIndex}`}
          variant="outline"
          className="h-auto w-full justify-start text-left whitespace-normal"
          onClick={() =>
            onUpdateCharacter(index, { name: item.name, role: item.role, emoji: item.emoji })}
        >
          {item.emoji} {item.name} - {item.role}
        </Button>
      ));
    }

    if (editTarget.kind === "location") {
      const index = editTarget.index;
      return drafts.locations.options.map((item, suggestionIndex) => (
        <Button
          key={`${item.name}-${suggestionIndex}`}
          variant="outline"
          className="h-auto w-full justify-start text-left whitespace-normal"
          onClick={() =>
            onUpdateLocation(index, {
              name: item.name,
              description: item.description,
              emoji: item.emoji,
            })}
        >
          {item.emoji} {item.name} - {item.description}
        </Button>
      ));
    }

    const index = editTarget.index;
    return drafts.encounters.options.map((item, suggestionIndex) => (
      <Button
        key={`${item.description}-${suggestionIndex}`}
        variant="outline"
        className="h-auto w-full justify-start text-left whitespace-normal"
        onClick={() => onUpdateEncounter(index, { description: item.description, type: item.type })}
      >
        {(() => {
          const { Icon, label } = getEncounterTypeMeta(item.type);
          return (
            <span className="inline-flex items-center gap-2">
              <Icon className="h-4 w-4" />
              <span className="text-muted-foreground">{item.type?.trim() || label}:</span>
              <span>{item.description}</span>
            </span>
          );
        })()}
      </Button>
    ));
  };

  const renderSheetEditor = () => {
    if (!editTarget) return null;

    if (editTarget.kind === "storyName") {
      return (
        <Input
          value={storySeed.storyName}
          onChange={(event) => onUpdateStoryName(event.target.value)}
          placeholder={t("playground.storyTitlePlaceholder", {
            defaultValue: "e.g., The Lost Sky Map",
          })}
        />
      );
    }

    if (editTarget.kind === "storyIntro") {
      return (
        <Textarea
          rows={6}
          value={storySeed.storyIntro}
          onChange={(event) => onUpdateStoryIntro(event.target.value)}
          placeholder={t("playground.storyIntroPlaceholder", {
            defaultValue: "e.g., On the edge of a misty harbor...",
          })}
        />
      );
    }

    if (editTarget.kind === "mainObjective") {
      return (
        <Textarea
          rows={4}
          value={storySeed.mainObjective.description}
          onChange={(event) => onUpdateMainObjective({ description: event.target.value })}
          placeholder={t("playground.mainQuestPlaceholder", {
            defaultValue: "e.g., Reach the lighthouse before the storm hits",
          })}
        />
      );
    }

    if (editTarget.kind === "twist") {
      return (
        <Textarea
          rows={4}
          value={storySeed.storyTwist}
          onChange={(event) => onUpdateStoryTwist(event.target.value)}
          placeholder={t("playground.twistPlaceholder", {
            defaultValue: "e.g., The map is fake and leads to a trap",
          })}
        />
      );
    }

    if (editTarget.kind === "sideQuest") {
      const item = storySeed.sideQuests[editTarget.index];
      return (
        <Textarea
          rows={4}
          value={item?.description ?? ""}
          onChange={(event) =>
            onUpdateSideQuest(editTarget.index, { description: event.target.value })}
          placeholder={t("playground.sideQuest", { defaultValue: "Side quest" })}
        />
      );
    }

    if (editTarget.kind === "encounter") {
      const item = storySeed.encounters[editTarget.index];
      return (
        <>
          <Textarea
            rows={4}
            value={item?.description ?? ""}
            onChange={(event) =>
              onUpdateEncounter(editTarget.index, { description: event.target.value })}
            placeholder={t("playground.encounters", { defaultValue: "Encounter" })}
          />
          <Input
            value={item?.type ?? ""}
            onChange={(event) => onUpdateEncounter(editTarget.index, { type: event.target.value })}
            placeholder={t("playground.type", { defaultValue: "Type (optional)" })}
          />
        </>
      );
    }

    if (editTarget.kind === "character") {
      const item = storySeed.characters[editTarget.index];
      return (
        <>
          <Input
            value={item?.emoji ?? ""}
            onChange={(event) => onUpdateCharacter(editTarget.index, { emoji: event.target.value })}
            placeholder={t("playground.emoji", { defaultValue: "Emoji" })}
          />
          <Input
            value={item?.name ?? ""}
            onChange={(event) => onUpdateCharacter(editTarget.index, { name: event.target.value })}
            placeholder={t("playground.name", { defaultValue: "Name" })}
          />
          <Input
            value={item?.role ?? ""}
            onChange={(event) => onUpdateCharacter(editTarget.index, { role: event.target.value })}
            placeholder={t("playground.role", { defaultValue: "Role" })}
          />
        </>
      );
    }

    const item = storySeed.locations[editTarget.index];
    return (
      <>
        <Input
          value={item?.emoji ?? ""}
          onChange={(event) => onUpdateLocation(editTarget.index, { emoji: event.target.value })}
          placeholder={t("playground.emoji", { defaultValue: "Emoji" })}
        />
        <Input
          value={item?.name ?? ""}
          onChange={(event) => onUpdateLocation(editTarget.index, { name: event.target.value })}
          placeholder={t("playground.name", { defaultValue: "Name" })}
        />
        <Textarea
          rows={4}
          value={item?.description ?? ""}
          onChange={(event) =>
            onUpdateLocation(editTarget.index, { description: event.target.value })}
          placeholder={t("playground.description", { defaultValue: "Description" })}
        />
      </>
    );
  };

  const getSheetTitle = () => {
    if (!editTarget) return "";
    if (editTarget.kind === "storyName") {
      return t("playground.storyTitle", { defaultValue: "Story Title" });
    }
    if (editTarget.kind === "storyIntro") {
      return t("playground.storyIntro", { defaultValue: "Story Intro" });
    }
    if (editTarget.kind === "mainObjective") {
      return t("playground.mainQuest", { defaultValue: "Main Quest" });
    }
    if (editTarget.kind === "twist") return t("playground.twist", { defaultValue: "Twist" });
    if (editTarget.kind === "sideQuest") {
      return `${t("playground.sideQuest", { defaultValue: "Side Quest" })} ${editTarget.index + 1}`;
    }
    if (editTarget.kind === "encounter") {
      return `${t("playground.encounters", { defaultValue: "Encounters" })} ${
        editTarget.index + 1
      }`;
    }
    if (editTarget.kind === "character") {
      return `${t("playground.characters", { defaultValue: "Characters" })} ${
        editTarget.index + 1
      }`;
    }
    return `${t("playground.locations", { defaultValue: "Locations" })} ${editTarget.index + 1}`;
  };

  const getSheetLoadingState = () => {
    if (!editTarget) return false;
    if (editTarget.kind === "storyName") return Boolean(generatingFields.storyName);
    if (editTarget.kind === "storyIntro") return Boolean(generatingFields.intro);
    if (editTarget.kind === "mainObjective") return Boolean(generatingFields.step3);
    if (editTarget.kind === "twist") return Boolean(generatingFields.twist);
    if (editTarget.kind === "sideQuest") return Boolean(generatingFields.sideQuest);
    if (editTarget.kind === "character") return Boolean(generatingFields.character);
    if (editTarget.kind === "location") return Boolean(generatingFields.location);
    return Boolean(generatingFields.encounter);
  };

  const getSuggestionCountForTarget = () => {
    if (!editTarget) return 0;
    if (editTarget.kind === "storyName") return drafts.storyName.options.length;
    if (editTarget.kind === "storyIntro") return drafts.storyIntro.options.length;
    if (editTarget.kind === "mainObjective") return drafts.mainObjective.options.length;
    if (editTarget.kind === "twist") return drafts.storyTwist.options.length;
    if (editTarget.kind === "sideQuest") return drafts.sideQuests.options.length;
    if (editTarget.kind === "character") return drafts.characters.options.length;
    if (editTarget.kind === "location") return drafts.locations.options.length;
    return drafts.encounters.options.length;
  };

  const ensureSuggestionsForTarget = useCallback(async () => {
    if (!editTarget) return;

    if (editTarget.kind === "storyName") {
      if (drafts.storyName.options.length === 0 && !generatingFields.storyName) {
        await onGenerateStoryNames({ forceFresh: false });
      }
      return;
    }

    if (editTarget.kind === "storyIntro") {
      if (drafts.storyIntro.options.length === 0 && !generatingFields.intro) {
        await onGenerateIntro("append", false);
      }
      return;
    }

    if (editTarget.kind === "mainObjective") {
      if (drafts.mainObjective.options.length === 0 && !generatingFields.step3) {
        await onGenerateMainObjective({ forceFresh: false });
      }
      return;
    }

    if (editTarget.kind === "twist") {
      if (drafts.storyTwist.options.length === 0 && !generatingFields.twist) {
        await onGenerateTwist("append", false);
      }
      return;
    }

    if (editTarget.kind === "sideQuest") {
      if (drafts.sideQuests.options.length === 0 && !generatingFields.sideQuest) {
        await onGenerateSideQuest("append", false);
      }
      return;
    }

    if (editTarget.kind === "character") {
      if (drafts.characters.options.length === 0 && !generatingFields.character) {
        await onGenerateCharacter("append", false);
      }
      return;
    }

    if (editTarget.kind === "location") {
      if (drafts.locations.options.length === 0 && !generatingFields.location) {
        await onGenerateLocation("append", false);
      }
      return;
    }

    if (drafts.encounters.options.length === 0 && !generatingFields.encounter) {
      await onGenerateEncounter("append", false);
    }
  }, [
    drafts.characters.options.length,
    drafts.encounters.options.length,
    drafts.locations.options.length,
    drafts.mainObjective.options.length,
    drafts.sideQuests.options.length,
    drafts.storyIntro.options.length,
    drafts.storyName.options.length,
    drafts.storyTwist.options.length,
    editTarget,
    generatingFields.character,
    generatingFields.encounter,
    generatingFields.intro,
    generatingFields.location,
    generatingFields.sideQuest,
    generatingFields.step3,
    generatingFields.storyName,
    generatingFields.twist,
    onGenerateCharacter,
    onGenerateEncounter,
    onGenerateIntro,
    onGenerateLocation,
    onGenerateMainObjective,
    onGenerateSideQuest,
    onGenerateStoryNames,
    onGenerateTwist,
  ]);

  useEffect(() => {
    if (!editTarget) return;
    void ensureSuggestionsForTarget();
  }, [editTarget, ensureSuggestionsForTarget]);

  return (
    <div className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-5xl space-y-6 sm:space-y-8">
        <div>
          <Button variant="ghost" onClick={onBack} className="mb-3">
            {t("playground.backToWizard", { defaultValue: "Back to Wizard" })}
          </Button>
          <div className="mb-2 flex items-center gap-2">
            <h1 className="text-2xl font-bold sm:text-3xl">
              {(storySeed.storyName ?? "").trim() ||
                t("playground.untitledStory", { defaultValue: "Untitled story" })}
            </h1>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setEditTarget({ kind: "storyName" })}
              aria-label={t("playground.storyTitle", { defaultValue: "Story Title" })}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-muted-foreground">
            {t("playground.subtitle", {
              defaultValue: "Click any field to open the editor sidebar with ready suggestions.",
            })}
          </p>
          {isInitialBulkLoading && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t("playground.initialLoading", { defaultValue: "Loading story sections..." })}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge variant="outline" className="text-xs">
              {t("playground.language", { defaultValue: "Language" })}: {settings.language}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {t("playground.ageGroup", { defaultValue: "Age Group" })}: {settings.ageGroup}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {t("playground.theme", { defaultValue: "Theme" })}:{" "}
              {settings.theme || t("playground.noTheme", { defaultValue: "No theme" })}
            </Badge>
            {settings.themeVariant && (
              <Badge variant="outline" className="text-xs">
                {t("playground.themeVariant", { defaultValue: "Theme Variant" })}:{" "}
                {settings.themeVariant}
              </Badge>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge variant="outline" className="text-xs">
              {sideQuestCount} {t("playground.quests", { defaultValue: "Quests" })}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {encounterCount} {t("playground.encounters", { defaultValue: "Encounters" })}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {characterCount} {t("playground.characters", { defaultValue: "Characters" })}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {locationCount} {t("playground.locations", { defaultValue: "Locations" })}
            </Badge>
          </div>
        </div>

        <Card className="p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-bold">{skillsTitle}</h2>
            <Input
              value={settings.mainCharacterName}
              onChange={(event) => onUpdateSettings({ mainCharacterName: event.target.value })}
              placeholder={t("playground.characterName", { defaultValue: "Character name" })}
              className="h-8 w-full sm:w-48"
            />
          </div>
          {settings.lockedSkills.length === 0
            ? (
              <div className="text-sm text-muted-foreground">
                {t("playground.noSkills", { defaultValue: "No skills selected" })}
              </div>
            )
            : (
              <div className="space-y-2">
                {settings.lockedSkills.map((skill, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-xl">{skill.emoji}</span>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{skill.name}</div>
                      {skill.application && (
                        <div className="text-xs text-muted-foreground">{skill.application}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
        </Card>

        <Card className="space-y-4 p-5 sm:p-6">
          <button
            type="button"
            className="w-full rounded-md border border-border/60 p-4 text-left hover:bg-muted/30"
            onClick={() => setEditTarget({ kind: "storyIntro" })}
          >
            <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("playground.storyIntro", { defaultValue: "Story Intro" })}
              <Pencil className="h-3.5 w-3.5" />
            </div>
            <div className="text-sm text-foreground/90 whitespace-pre-wrap">
              {(storySeed.storyIntro ?? "").trim() ||
                t("playground.storyIntroEmpty", {
                  defaultValue: "Add a short intro to set the quest in motion.",
                })}
            </div>
          </button>
        </Card>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">
              {t("playground.quests", { defaultValue: "Quests" })}
            </h2>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {(generatingFields.sideQuest || isInitialBulkLoading) && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onUpdateSlotCount("sideQuests", -1)}
                aria-label={t("story.removeSlot", { defaultValue: "Remove slot" })}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => void handleAddPrefilledSlot("sideQuests")}
                aria-label={t("story.addSlot", { defaultValue: "Add slot" })}
              >
                <Plus className="h-4 w-4" />
              </Button>
              {sideQuestCount > 0 && (
                <Badge variant="outline" className="text-xs">{sideQuestCount}</Badge>
              )}
            </div>
          </div>

          <Card className="p-5 space-y-3 border-primary/30 bg-primary/5">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("playground.mainQuest", { defaultValue: "Main Quest" })}
            </div>
            <button
              type="button"
              className="w-full rounded-md border border-border/60 p-3 text-left hover:bg-background/80"
              onClick={() => setEditTarget({ kind: "mainObjective" })}
            >
              <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                {t("playground.mainQuest", { defaultValue: "Main Quest" })}
                <Pencil className="h-3.5 w-3.5" />
              </div>
              <div className="text-sm whitespace-pre-wrap">
                {(storySeed.mainObjective.description ?? "").trim() ||
                  t("playground.mainQuestEmpty", { defaultValue: "Add a main objective" })}
              </div>
            </button>
            <button
              type="button"
              className="w-full rounded-md border border-border/60 p-3 text-left hover:bg-background/80"
              onClick={() => setEditTarget({ kind: "twist" })}
            >
              <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                {t("playground.twist", { defaultValue: "Twist" })}
                <Pencil className="h-3.5 w-3.5" />
              </div>
              <div className="text-sm whitespace-pre-wrap">
                {(storySeed.storyTwist ?? "").trim() ||
                  t("playground.twistEmpty", {
                    defaultValue: "Add a twist to complicate the quest.",
                  })}
              </div>
            </button>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleAddPrefilledSlot("sideQuests")}
              >
                <Plus className="h-4 w-4 mr-1" />
                {t("playground.addSideQuest", { defaultValue: "Add Side Quest" })}
              </Button>
            </div>
            <SkillUsageTags
              skillUses={storySeed.mainObjective.skillUses || []}
              disabled={!canAddSkillUses}
              isLoading={Boolean(activeSkillUseLoaders.objective)}
              onRemove={(usage) =>
                onUpdateMainObjective({
                  skillUses: removeSkillUse(storySeed.mainObjective.skillUses || [], usage),
                })}
              onAddMore={() =>
                withSkillUsageLoader("objective", async () => {
                  const generated = await onGenerateSkillUses("objective", undefined, {
                    forceFresh: true,
                    targetDescription: storySeed.mainObjective.description,
                  });
                  onUpdateMainObjective({
                    skillUses: appendSkillUses(storySeed.mainObjective.skillUses || [], generated),
                  });
                })}
            />
          </Card>

          <div className="grid gap-3 sm:grid-cols-2">
            {visibleSideQuests.length === 0 &&
              (generatingFields.sideQuest || isInitialBulkLoading) && (
              <>{renderSkeletonCards(Math.max(2, settings.slotCounts.sideQuests))}</>
            )}
            {visibleSideQuests.map(({ quest, index }) => (
              <Card key={index} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <button
                    type="button"
                    className="flex-1 text-left"
                    onClick={() =>
                      setEditTarget({ kind: "sideQuest", index })}
                  >
                    <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("playground.sideQuest", { defaultValue: "Side Quest" })} {index + 1}
                      <Pencil className="h-3.5 w-3.5" />
                    </div>
                    <div className="text-sm whitespace-pre-wrap">{quest.description}</div>
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      onToggleLock("sideQuests", index)}
                    aria-label={t("editor.removeFromStory", { defaultValue: "Remove from story" })}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <SkillUsageTags
                  skillUses={quest.skillUses || []}
                  disabled={!canAddSkillUses}
                  isLoading={Boolean(activeSkillUseLoaders[`sideQuest-${index}`])}
                  onRemove={(usage) =>
                    onUpdateSideQuest(index, {
                      skillUses: removeSkillUse(quest.skillUses || [], usage),
                    })}
                  onAddMore={() =>
                    withSkillUsageLoader(`sideQuest-${index}`, async () => {
                      const generated = await onGenerateSkillUses("sideQuest", index, {
                        forceFresh: true,
                        targetDescription: quest.description,
                      });
                      onUpdateSideQuest(index, {
                        skillUses: appendSkillUses(quest.skillUses || [], generated),
                      });
                    })}
                />
              </Card>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card className="space-y-4 p-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {t("playground.characters", { defaultValue: "Characters" })}
              </h2>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {(generatingFields.character || isInitialBulkLoading) && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onUpdateSlotCount("characters", -1)}
                  aria-label={t("story.removeSlot", { defaultValue: "Remove slot" })}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => void handleAddPrefilledSlot("characters")}
                  aria-label={t("story.addSlot", { defaultValue: "Add slot" })}
                >
                  <Plus className="h-4 w-4" />
                </Button>
                {characterCount > 0 && (
                  <Badge variant="outline" className="text-xs">{characterCount}</Badge>
                )}
              </div>
            </div>
            <div className="grid gap-2">
              {storySeed.characters.map((character, index) => (
                <Card key={index} className="p-3">
                  {character.name
                    ? (
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <button
                            type="button"
                            className="flex-1 text-left"
                            onClick={() => setEditTarget({ kind: "character", index })}
                          >
                            <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              {t("playground.character", { defaultValue: "Character" })} {index + 1}
                              <Pencil className="h-3.5 w-3.5" />
                            </div>
                            <div className="text-sm font-semibold">
                              {character.emoji} {character.name}
                            </div>
                            <div className="text-xs text-muted-foreground">{character.role}</div>
                          </button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onToggleLock("characters", index)}
                            aria-label={t("editor.removeFromStory", {
                              defaultValue: "Remove from story",
                            })}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <SkillUsageTags
                          skillUses={character.skillUses || []}
                          disabled={!canAddSkillUses}
                          isLoading={Boolean(activeSkillUseLoaders[`character-${index}`])}
                          onRemove={(usage) =>
                            onUpdateCharacter(index, {
                              skillUses: removeSkillUse(character.skillUses || [], usage),
                            })}
                          onAddMore={() =>
                            withSkillUsageLoader(`character-${index}`, async () => {
                              const generated = await onGenerateSkillUses("character", index, {
                                forceFresh: true,
                                targetDescription: `${character.name} (${character.role})`,
                              });
                              onUpdateCharacter(index, {
                                skillUses: appendSkillUses(character.skillUses || [], generated),
                              });
                            })}
                        />
                      </div>
                    )
                    : (generatingFields.character || isInitialBulkLoading)
                    ? <div className="h-12 w-full animate-pulse rounded bg-muted" />
                    : renderEmpty(
                      t("playground.emptyCharacterSlot", { defaultValue: "Empty character slot" }),
                    )}
                </Card>
              ))}
            </div>
          </Card>

          <Card className="space-y-4 p-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {t("playground.locations", { defaultValue: "Locations" })}
              </h2>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {(generatingFields.location || isInitialBulkLoading) && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onUpdateSlotCount("locations", -1)}
                  aria-label={t("story.removeSlot", { defaultValue: "Remove slot" })}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => void handleAddPrefilledSlot("locations")}
                  aria-label={t("story.addSlot", { defaultValue: "Add slot" })}
                >
                  <Plus className="h-4 w-4" />
                </Button>
                {locationCount > 0 && (
                  <Badge variant="outline" className="text-xs">{locationCount}</Badge>
                )}
              </div>
            </div>
            <div className="grid gap-2">
              {storySeed.locations.map((location, index) => (
                <Card key={index} className="p-3">
                  {location.name
                    ? (
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <button
                            type="button"
                            className="flex-1 text-left"
                            onClick={() => setEditTarget({ kind: "location", index })}
                          >
                            <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              {t("playground.location", { defaultValue: "Location" })} {index + 1}
                              <Pencil className="h-3.5 w-3.5" />
                            </div>
                            <div className="text-sm font-semibold">
                              {location.emoji} {location.name}
                            </div>
                            <div className="text-xs text-muted-foreground whitespace-pre-wrap">
                              {location.description}
                            </div>
                          </button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onToggleLock("locations", index)}
                            aria-label={t("editor.removeFromStory", {
                              defaultValue: "Remove from story",
                            })}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <SkillUsageTags
                          skillUses={location.skillUses || []}
                          disabled={!canAddSkillUses}
                          isLoading={Boolean(activeSkillUseLoaders[`location-${index}`])}
                          onRemove={(usage) =>
                            onUpdateLocation(index, {
                              skillUses: removeSkillUse(location.skillUses || [], usage),
                            })}
                          onAddMore={() =>
                            withSkillUsageLoader(`location-${index}`, async () => {
                              const generated = await onGenerateSkillUses("location", index, {
                                forceFresh: true,
                                targetDescription: `${location.name} - ${location.description}`,
                              });
                              onUpdateLocation(index, {
                                skillUses: appendSkillUses(location.skillUses || [], generated),
                              });
                            })}
                        />
                      </div>
                    )
                    : (generatingFields.location || isInitialBulkLoading)
                    ? <div className="h-12 w-full animate-pulse rounded bg-muted" />
                    : renderEmpty(
                      t("playground.emptyLocationSlot", { defaultValue: "Empty location slot" }),
                    )}
                </Card>
              ))}
            </div>
          </Card>
        </div>

        <Card className="space-y-4 p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">
              {t("playground.encounters", { defaultValue: "Encounters" })}
            </h2>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {(generatingFields.encounter || isInitialBulkLoading) && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onUpdateSlotCount("encounters", -1)}
                aria-label={t("story.removeSlot", { defaultValue: "Remove slot" })}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => void handleAddPrefilledSlot("encounters")}
                aria-label={t("story.addSlot", { defaultValue: "Add slot" })}
              >
                <Plus className="h-4 w-4" />
              </Button>
              {encounterCount > 0 && (
                <Badge variant="outline" className="text-xs">{encounterCount}</Badge>
              )}
            </div>
          </div>
          <div className="grid gap-2">
            {storySeed.encounters.map((encounter, index) => (
              <Card key={index} className="p-3 space-y-2">
                {encounter.description
                  ? (
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <button
                          type="button"
                          className="flex-1 text-left"
                          onClick={() => setEditTarget({ kind: "encounter", index })}
                        >
                          <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {(() => {
                              const { Icon, label } = getEncounterTypeMeta(encounter.type);
                              return (
                                <span className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal">
                                  <Icon className="h-3 w-3" />
                                  {encounter.type?.trim() || label}
                                </span>
                              );
                            })()}
                            {t("playground.encounter", { defaultValue: "Encounter" })} {index + 1}
                            <Pencil className="h-3.5 w-3.5" />
                          </div>
                          <div className="text-sm whitespace-pre-wrap">{encounter.description}</div>
                        </button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            onToggleLock("encounters", index)}
                          aria-label={t("editor.removeFromStory", {
                            defaultValue: "Remove from story",
                          })}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <SkillUsageTags
                        skillUses={encounter.skillUses || []}
                        disabled={!canAddSkillUses}
                        isLoading={Boolean(activeSkillUseLoaders[`encounter-${index}`])}
                        onRemove={(usage) =>
                          onUpdateEncounter(index, {
                            skillUses: removeSkillUse(encounter.skillUses || [], usage),
                          })}
                        onAddMore={() =>
                          withSkillUsageLoader(`encounter-${index}`, async () => {
                            const generated = await onGenerateSkillUses("encounter", index, {
                              forceFresh: true,
                              targetDescription: encounter.description,
                            });
                            onUpdateEncounter(index, {
                              skillUses: appendSkillUses(encounter.skillUses || [], generated),
                            });
                          })}
                      />
                    </>
                  )
                  : (generatingFields.encounter || isInitialBulkLoading)
                  ? <div className="h-12 w-full animate-pulse rounded bg-muted" />
                  : renderEmpty(
                    t("playground.emptyEncounterSlot", { defaultValue: "Empty encounter slot" }),
                  )}
              </Card>
            ))}
          </div>
        </Card>
      </div>

      <Sheet open={Boolean(editTarget)} onOpenChange={(open) => !open && setEditTarget(null)}>
        <SheetContent className="overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{getSheetTitle()}</SheetTitle>
            <SheetDescription>
              {t("playground.editSidebarDescription", {
                defaultValue: "Edit this field directly or pick from proactive suggestions.",
              })}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            {renderSheetEditor()}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <span>{t("playground.suggestions", { defaultValue: "Suggestions" })}</span>
                {getSheetLoadingState()
                  ? <span>{t("editor.generating", { defaultValue: "Generating..." })}</span>
                  : (
                    <span>
                      {getSuggestionCountForTarget() > 0
                        ? t("playground.suggestionsReadyCount", {
                          defaultValue: "Ready: {{count}}",
                          count: getSuggestionCountForTarget(),
                        })
                        : t("playground.suggestionsEmpty", {
                          defaultValue: "No cached suggestions",
                        })}
                    </span>
                  )}
              </div>
              <Button variant="outline" size="sm" onClick={() => void addMoreForTarget()}>
                <Sparkles className="mr-1 h-4 w-4" />
                {t("wizard.addMore", { defaultValue: "Add More" })}
              </Button>
            </div>
            {getSheetLoadingState() && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t("editor.generating", { defaultValue: "Generating..." })}
              </div>
            )}
            <div className="space-y-2">{renderSheetSuggestions()}</div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
